import type { QueryClient } from '@tanstack/react-query';
import type {
  SnippetsSnippetDetailResponse,
  SnippetsSnippetUsageResponse,
  SnippetsSnippetsListResponse,
} from '@customerio/hydra-ui-api';
import {
  mergeSnippetUsage,
  parseSnippet,
  parseSnippetWithUsageLoaded,
  snippetKeys,
  type Snippet,
  type SnippetMessageItem,
} from './entity';

/**
 * Cluster propagation helpers for the snippet detail/usage endpoints.
 *
 * The list endpoint is bare (`{ snippets, meta }`) — no sideloads — so
 * its parsed shape is just the array. Detail and usage both sideload a
 * `message_items` array of `TemplateItem`s; we don't write those into a
 * detail cache slot because templates are owned by the (future)
 * `template`/`message-item` cluster, but we hand them back to the caller
 * so it can compute usage counts without re-fetching.
 */

export interface ParsedSnippetsListResponse {
  snippets: readonly Snippet[];
  meta?: SnippetsSnippetsListResponse['meta'];
}

export function parseSnippetsListResponse(raw: SnippetsSnippetsListResponse): ParsedSnippetsListResponse {
  return {
    snippets: raw.snippets.map(parseSnippet),
    meta: raw.meta,
  };
}

export function populateSnippetsList(qc: QueryClient, workspaceId: number, response: ParsedSnippetsListResponse): void {
  for (const s of response.snippets) qc.setQueryData(snippetKeys.detail(workspaceId, s.id), s);
}

export interface ParsedSnippetDetailResponse {
  snippet: Snippet;
  message_items: readonly SnippetMessageItem[];
}

export function parseSnippetDetailResponse(raw: SnippetsSnippetDetailResponse): ParsedSnippetDetailResponse {
  // `usage_loaded` is a top-level wire field; Ember historically folded
  // it onto the snippet model, so we do the same to give consumers a
  // single source of truth without an extra wrapper type.
  const snippet = parseSnippetWithUsageLoaded(raw.snippet, raw.usage_loaded);
  return { snippet, message_items: raw.message_items };
}

export function populateSnippetDetail(qc: QueryClient, workspaceId: number, parsed: ParsedSnippetDetailResponse): void {
  qc.setQueryData(snippetKeys.detail(workspaceId, parsed.snippet.id), parsed.snippet);
}

export interface ParsedSnippetUsageResponse {
  snippet: ParsedSnippetUsageDelta;
  message_items: readonly SnippetMessageItem[];
  continuation: number;
  more: boolean;
}

export interface ParsedSnippetUsageDelta {
  message_item_ids: string[];
  in_use: boolean;
}

export function parseSnippetUsageResponse(raw: SnippetsSnippetUsageResponse): ParsedSnippetUsageResponse {
  return {
    snippet: { message_item_ids: raw.snippet.message_item_ids, in_use: raw.snippet.in_use },
    message_items: raw.message_items ?? [],
    continuation: raw.continuation,
    more: raw.more,
  };
}

/**
 * Merge a usage response into the cached snippet detail (if any). The
 * usage endpoint returns only delta fields, not the snippet's core
 * attributes, so we need an existing entry to merge against.
 */
export function populateSnippetUsage(
  qc: QueryClient,
  workspaceId: number,
  snippetId: string,
  parsed: ParsedSnippetUsageResponse,
): void {
  const existing = qc.getQueryData<Snippet>(snippetKeys.detail(workspaceId, snippetId));
  if (existing == null) return;
  qc.setQueryData(snippetKeys.detail(workspaceId, snippetId), mergeSnippetUsage(existing, parsed.snippet));
}
